import Vue from 'vue'
import cloneDeep from 'lodash/cloneDeep'

/**
 * Config Bus — mutations (Sprint 16, epic REFACTOR-030).
 *
 * Tutte le scritture passano da qui (niente mutazioni di state fuori da mutation,
 * fix dell'anti-pattern rilevato da INVESTIGATION-002 in `saveConfig`).
 *
 * Punto chiave: `SET_VALUE` muta IN-PLACE via `Vue.set` invece di riassegnare
 * l'intero oggetto. Così cambiare una chiave invalida solo i lettori di QUELLA
 * chiave (getter `value(key)`), non tutti i consumer di config → niente cascata,
 * niente bisogno di deep-watch. Vedi `docs/architecture/config-bus.md` §3.
 */
const mutations = {
    // Scrittura granulare reattiva di una singola chiave.
    SET_VALUE(state, { scope = 'user', key, value }) {
        const target = scope === 'company' ? state.company : state.user
        Vue.set(target, key, value)
    },

    // Popolamento iniziale (load) o reset: assegna il blob grezzo dal backend.
    // NB: i default NON vengono "cotti" qui — si applicano in lettura nel getter
    // `value(key)`. Così `blob` resta identico al legacy (parità per i reader del
    // blob intero). Riassegnazione voluta e una-tantum (non è un hot-path).
    HYDRATE(state, { scope = 'user', blob }) {
        if (scope === 'company') {
            state.company = { ...blob }
        } else {
            state.user = { ...blob }
        }
    },

    // Sprint 19 (REFACTOR-030): rimossa `MERGE` (path legacy di `saveLegacy`, ritirata
    // con gli shim — tutti i writer usano `SET_VALUE` granulare).

    SET_COMPANY_ID(state, companyId) {
        state.companyId = companyId
    },

    SET_STATUS(state, patch) {
        state.status = { ...state.status, ...patch }
    },

    // Marca una chiave come "da persistere" e ne conserva lo snapshot precedente
    // per l'eventuale rollback. Lo snapshot si registra solo alla PRIMA modifica
    // del ciclo (così un rollback torna al valore confermato col backend).
    MARK_DIRTY(state, { scope = 'user', key, prev }) {
        const dirty = state.dirty
        if (!dirty[scope]) Vue.set(dirty, scope, {})
        if (!Object.prototype.hasOwnProperty.call(dirty[scope], key)) {
            // Snapshot CLONATO: alcuni writer (es. sort/activeFilters) committano la
            // stessa reference che mutano in-place lato componente → senza clone lo
            // snapshot punterebbe all'oggetto vivo e ROLLBACK sarebbe un no-op (su POST
            // fallito lo state resterebbe sul valore non persistito). cloneDeep isola il
            // valore confermato col backend; sui primitivi è un passthrough.
            Vue.set(dirty[scope], key, cloneDeep(prev))
        }
    },

    CLEAR_DIRTY(state, scope = 'user') {
        Vue.set(state.dirty, scope, {})
    },

    // Ripristina le chiavi dirty ai loro snapshot (fallimento del POST).
    ROLLBACK(state, scope = 'user') {
        const snapshots = state.dirty[scope] || {}
        const target = scope === 'company' ? state.company : state.user
        for (const key of Object.keys(snapshots)) {
            // Snapshot `undefined` ⟺ la chiave era ASSENTE al primo commit (MARK_DIRTY
            // registra `undefined` solo quando hasOwnProperty era false; HYDRATE/SET_VALUE
            // non producono mai valori undefined). Ripristinare con Vue.set lascerebbe la
            // chiave PRESENTE con undefined → `value(key)` smetterebbe di applicare il
            // default di schema. La cancelliamo per ripristinare l'assenza originale.
            if (snapshots[key] === undefined) {
                Vue.delete(target, key)
            } else {
                Vue.set(target, key, snapshots[key])
            }
        }
        Vue.set(state.dirty, scope, {})
    }
}

export default mutations
